import React, { useState, useMemo } from "react";

const CommonInfo = ({
  data,
  allProducts = [],
  baoGiaList = [],
  thongBaoPhiList = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPhiExpanded, setIsPhiExpanded] = useState(false);

  const totalProducts = allProducts.length;
  const completedProducts = allProducts.filter(
    (product) =>
      product.trangThai === "Hoàn thành" ||
      product.trangThai === "Đã trả kết quả",
  ).length;

  const getProgressBadgeColor = () => {
    if (completedProducts === totalProducts && totalProducts > 0) {
      return "bg-green-100 text-green-700";
    } else if (completedProducts > 0 && completedProducts < totalProducts) {
      return "bg-blue-100 text-blue-700";
    } else {
      return "bg-gray-100 text-gray-600";
    }
  };

  // Visibility logic
  const lateStageStatuses = ["Chờ nộp phí", "Hoàn thành", "Đã trả kết quả"];
  const hasLateStageProduct = allProducts.some((p) =>
    lateStageStatuses.includes(p.trangThai),
  );

  const shouldShowThongBaoPhi =
    thongBaoPhiList?.length > 0 && hasLateStageProduct;

  const finalShouldShowBaoGia =
    !shouldShowThongBaoPhi &&
    baoGiaList?.length > 0 &&
    allProducts.every((p) => !lateStageStatuses.includes(p.trangThai));

  // Group thongBaoPhiList by soThongBaoNopTien
  const groupedThongBaoPhi = useMemo(() => {
    if (!thongBaoPhiList || thongBaoPhiList.length === 0) return {};

    return thongBaoPhiList.reduce((acc, item) => {
      const key = item.soThongBaoNopTien || "Chưa có số thông báo";
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
  }, [thongBaoPhiList]);

  // Calculate financials for Bao Gia
  const subTotal = baoGiaList.reduce(
    (sum, item) => sum + (item.thanhTien || 0),
    0,
  );
  const vatAmount = subTotal * 0.1;
  const grandTotal = subTotal + vatAmount;

  // Calculate financials for Thong Bao Phi (Subtotal + 10% VAT)
  const phiSubTotal = thongBaoPhiList.reduce(
    (sum, item) => sum + (item.thanhTien || 0),
    0,
  );
  const phiVatAmount = phiSubTotal * 0.1;
  const phiGrandTotal = phiSubTotal + phiVatAmount;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN").format(amount);
  };

  const InfoRow = ({
    label,
    value,
    icon,
    customValueClass,
    isBadge,
    isLastRow,
  }) => (
    <div
      className={`flex items-start py-3 ${isLastRow ? "" : "border-b border-neutral-divider"}`}
    >
      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-primary-main">
        {icon}
      </div>
      <div className="ml-3 flex-1">
        <dt className="text-sm font-semibold text-neutral-textSecondary font-body">
          {label}
        </dt>
        <dd className="mt-1">
          {isBadge ? (
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${customValueClass}`}
            >
              {value}
            </span>
          ) : (
            <span
              className={`text-base font-body ${customValueClass || "text-neutral-textPrimary"}`}
            >
              {value || "N/A"}
            </span>
          )}
        </dd>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-primary-50 to-neutral-bgPaper p-6 rounded-md-tqc border border-neutral-borderAccent mb-6 shadow-sm-tqc">
      <h3 className="text-xl font-bold text-neutral-textPrimary mb-4 flex items-center font-heading">
        <svg
          className="w-6 h-6 text-primary-main mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        Thông tin hồ sơ
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Row 1: Đơn vị đề nghị | Đơn vị xuất hóa đơn */}
        <InfoRow
          label="Đơn vị đề nghị"
          value={data.donViDeNghi}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
        />

        <InfoRow
          label="Đơn vị xuất hóa đơn"
          value={data.tenDonVibaoPhi}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
        />

        {/* Row 2: Ngày tiếp nhận | Người tiếp nhận */}
        <InfoRow
          label="Ngày tiếp nhận"
          value={data.ngayTiepNhan}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          }
        />

        <InfoRow
          label="Người tiếp nhận"
          value={data.nguoiTiepNhan}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          }
        />

        {/* Row 3: Người nộp hồ sơ | Tiến độ hoàn thành */}
        <InfoRow
          label="Người nộp hồ sơ"
          value={data.nguoiLienHe}
          isLastRow={true}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          }
        />

        {totalProducts > 0 && (
          <InfoRow
            label="Tiến độ hoàn thành"
            value={`${completedProducts}/${totalProducts} sản phẩm`}
            customValueClass={getProgressBadgeColor()}
            isBadge={true}
            isLastRow={true}
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />
        )}
      </div>

      {/* Thông báo phí Summary Block */}
      {shouldShowThongBaoPhi && (
        <>
          {(() => {
            const isAllCompleted = totalProducts > 0 && completedProducts === totalProducts;
            
            const phiTheme = isAllCompleted 
              ? {
                  bg: "bg-green-50",
                  border: "border-green-200",
                  iconText: "text-green-600",
                  titleText: "text-green-700",
                  amountText: "text-green-900",
                  btnBg: "bg-green-600 hover:bg-green-700",
                  titleLabel: "Đã nộp phí"
                }
              : {
                  bg: "bg-amber-50",
                  border: "border-amber-200",
                  iconText: "text-amber-600",
                  titleText: "text-amber-700",
                  amountText: "text-amber-900",
                  btnBg: "bg-amber-600 hover:bg-amber-700",
                  titleLabel: "Thông báo phí"
                };

            return (
              <div className={`mt-4 ${phiTheme.bg} border ${phiTheme.border} rounded-lg p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg
                      className={`w-6 h-6 ${phiTheme.iconText}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <p className={`text-sm font-semibold ${phiTheme.titleText} font-body`}>
                        {phiTheme.titleLabel}
                      </p>
                      <p className={`text-lg font-bold ${phiTheme.amountText} font-heading`}>
                        {formatCurrency(phiGrandTotal)} VNĐ
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsPhiExpanded(!isPhiExpanded)}
                    className={`px-4 py-2 ${phiTheme.btnBg} text-white rounded-md transition-colors font-semibold text-sm font-body flex items-center gap-2`}
                  >
                    <span>{isPhiExpanded ? "Thu gọn" : "Xem chi tiết"}</span>
                    <svg
                      className={`w-4 h-4 transition-transform duration-300 ${isPhiExpanded ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Inline Expandable Details for Thông báo phí */}
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              isPhiExpanded
                ? "grid-rows-[1fr] opacity-100 mt-4"
                : "grid-rows-[0fr] opacity-0 mt-0"
            }`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-amber-100 pt-4">
                {Object.entries(groupedThongBaoPhi).map(
                  ([soThongBao, items], groupIndex) => {
                    const groupSubTotal = items.reduce(
                      (sum, item) => sum + (item.thanhTien || 0),
                      0,
                    );
                    const groupVatAmount = groupSubTotal * 0.1;
                    const groupGrandTotal = groupSubTotal + groupVatAmount;

                    const relatedProducts = allProducts.filter(
                      (product) => product.soThongBaoNopTien === soThongBao,
                    );
                    const relatedProductSymbols = relatedProducts
                      .map((p) => p.kyHieu)
                      .filter(Boolean)
                      .join(", ");

                    const isGroupPaid = relatedProducts.length > 0 && relatedProducts.every(
                      p => p.trangThai === "Hoàn thành" || p.trangThai === "Đã trả kết quả"
                    );

                    return (
                      <div
                        key={groupIndex}
                        className={groupIndex > 0 ? "mt-6" : ""}
                      >
                        <div className={`mb-3 mt-5 border-l-4 pl-3 ${isGroupPaid ? 'border-green-500' : 'border-amber-500'}`}>
                          <h4 className="font-bold text-gray-800 text-base uppercase font-heading tracking-wide flex items-center gap-2">
                            Số thông báo: {soThongBao}
                            {isGroupPaid && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 normal-case">
                                Đã nộp phí
                              </span>
                            )}
                          </h4>
                          {relatedProducts.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1 font-body">
                              <span className="font-semibold text-gray-700">Sản phẩm:</span> {relatedProductSymbols}
                            </p>
                          )}
                        </div>

                        {/* Desktop Table View */}
                        <table className="hidden md:table w-full border-collapse border border-gray-300 mb-4">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                                STT
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                                Loại phí dịch vụ
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                                Đơn giá (đ)
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                                Số lượng
                              </th>
                              <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                                Thành tiền (đ)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => (
                              <tr key={index}>
                                <td className="border border-gray-300 px-4 py-2 text-center font-body">
                                  {item.stt}
                                </td>
                                <td className="border border-gray-300 px-4 py-2 font-body">
                                  {item.loaiPhi}
                                </td>
                                <td className="border border-gray-300 px-4 py-2 text-right font-body">
                                  {formatCurrency(item.donGia || 0)}
                                  {item.ghiChu && `/${item.ghiChu}`}
                                </td>
                                <td className="border border-gray-300 px-4 py-2 text-center font-body">
                                  {item.soLuong || 0}
                                </td>
                                <td className="border border-gray-300 px-4 py-2 text-right font-body">
                                  {formatCurrency(item.thanhTien || 0)}
                                </td>
                              </tr>
                            ))}

                            {/* Summary Rows for this group */}
                            <tr className="bg-gray-50">
                              <td
                                colSpan="4"
                                className="border border-gray-300 px-4 py-2 text-right font-bold font-body"
                              >
                                Cộng
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-right font-bold font-body">
                                {formatCurrency(groupSubTotal)}
                              </td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td
                                colSpan="4"
                                className="border border-gray-300 px-4 py-2 text-right font-bold font-body"
                              >
                                Thuế GTGT (10%)
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-right font-bold font-body">
                                {formatCurrency(groupVatAmount)}
                              </td>
                            </tr>
                            <tr className="bg-gray-100">
                              <td
                                colSpan="4"
                                className="border border-gray-300 px-4 py-2 text-right font-bold font-body"
                              >
                                Tổng cộng
                              </td>
                              <td className="border border-gray-300 px-4 py-2 text-right font-bold text-lg font-body">
                                {formatCurrency(groupGrandTotal)}
                              </td>
                            </tr>
                          </tbody>
                        </table>

                        {/* Mobile Card View */}
                        <div className="md:hidden flex flex-col gap-3 mb-4">
                          {items.map((item, index) => (
                            <div
                              key={index}
                              className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                            >
                              <div className="font-bold text-gray-800 text-sm mb-2 leading-snug">
                                {item.stt}. {item.loaiPhi}
                              </div>
                              <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
                                <span>
                                  Đơn giá: {formatCurrency(item.donGia || 0)}
                                  {item.ghiChu ? `/${item.ghiChu}` : ""}
                                </span>
                                <span className="font-medium bg-gray-100 px-2 py-0.5 rounded">
                                  SL: {item.soLuong || 0}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
                                <span className="text-gray-500">
                                  Thành tiền:
                                </span>
                                <span className="font-bold text-amber-700">
                                  {formatCurrency(item.thanhTien || 0)} đ
                                </span>
                              </div>
                            </div>
                          ))}

                          {/* Mobile Summary Card for this group */}
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-gray-600">Cộng:</span>
                              <span className="font-semibold text-gray-800">
                                {formatCurrency(groupSubTotal)}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-gray-600">
                                Thuế GTGT (10%):
                              </span>
                              <span className="font-semibold text-gray-800">
                                {formatCurrency(groupVatAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between text-base pt-3 border-t border-amber-200 mt-1">
                              <span className="font-bold text-gray-900">
                                Tổng cộng:
                              </span>
                              <span className="font-bold text-amber-700">
                                {formatCurrency(groupGrandTotal)} VNĐ
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Báo giá dự kiến Summary Block */}
      {finalShouldShowBaoGia && (
        <>
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-blue-700 font-body">
                    Báo giá dự kiến
                  </p>
                  <p className="text-lg font-bold text-blue-900 font-heading">
                    {formatCurrency(grandTotal)} VNĐ
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-semibold text-sm font-body flex items-center gap-2"
              >
                <span>{isExpanded ? "Thu gọn" : "Xem chi tiết"}</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Inline Expandable Details */}
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              isExpanded
                ? "grid-rows-[1fr] opacity-100 mt-4"
                : "grid-rows-[0fr] opacity-0 mt-0"
            }`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-blue-100 pt-4">
                {/* Desktop Table View */}
                <table className="hidden md:table w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                        STT
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                        Loại phí dịch vụ
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                        Đơn giá (đ)
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                        Số lượng
                      </th>
                      <th className="border border-gray-300 px-4 py-2 text-center font-bold font-body">
                        Thành tiền (đ)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {baoGiaList.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2 text-center font-body">
                          {item.stt}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 font-body">
                          {item.loaiPhi}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-right font-body">
                          {formatCurrency(item.donGia || 0)}
                          {item.ghiChu && `/${item.ghiChu}`}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-center font-body">
                          {item.soLuong || 0}
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-right font-body">
                          {formatCurrency(item.thanhTien || 0)}
                        </td>
                      </tr>
                    ))}

                    {/* Summary Rows */}
                    <tr className="bg-gray-50">
                      <td
                        colSpan="4"
                        className="border border-gray-300 px-4 py-2 text-right font-bold font-body"
                      >
                        Cộng
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-right font-bold font-body">
                        {formatCurrency(subTotal)}
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td
                        colSpan="4"
                        className="border border-gray-300 px-4 py-2 text-right font-bold font-body"
                      >
                        Thuế GTGT (10%)
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-right font-bold font-body">
                        {formatCurrency(vatAmount)}
                      </td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td
                        colSpan="4"
                        className="border border-gray-300 px-4 py-2 text-right font-bold font-body"
                      >
                        Tổng cộng
                      </td>
                      <td className="border border-gray-300 px-4 py-2 text-right font-bold text-lg font-body">
                        {formatCurrency(grandTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="md:hidden flex flex-col gap-3">
                  {baoGiaList.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                    >
                      <div className="font-bold text-gray-800 text-sm mb-2 leading-snug">
                        {item.stt}. {item.loaiPhi}
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
                        <span>
                          Đơn giá: {formatCurrency(item.donGia || 0)}
                          {item.ghiChu ? `/${item.ghiChu}` : ""}
                        </span>
                        <span className="font-medium bg-gray-100 px-2 py-0.5 rounded">
                          SL: {item.soLuong || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
                        <span className="text-gray-500">Thành tiền:</span>
                        <span className="font-bold text-blue-700">
                          {formatCurrency(item.thanhTien || 0)} đ
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Mobile Summary Card */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Cộng:</span>
                      <span className="font-semibold text-gray-800">
                        {formatCurrency(subTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Thuế GTGT (10%):</span>
                      <span className="font-semibold text-gray-800">
                        {formatCurrency(vatAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base pt-3 border-t border-blue-200 mt-1">
                      <span className="font-bold text-gray-900">
                        Tổng cộng:
                      </span>
                      <span className="font-bold text-blue-700">
                        {formatCurrency(grandTotal)} VNĐ
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CommonInfo;
